import React, { useEffect, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { CircularProgress } from '@mui/material';
import contactsService from '../../axios/contactsService';
import { ContactPreviewTable } from './ContactPreviewTable';
import styles from './ContactUploadModal.module.css';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // ~60s

const TERMINAL_STATUSES = ['SUCCEEDED', 'PARTIAL', 'FAILED'];

export const ContactUploadStatus = ({ uploadId, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timeoutHandle;

    const poll = async () => {
      attempts += 1;

      try {
        const result = await contactsService.pollUploadStatus(uploadId);
        if (cancelled) return;

        if (result && TERMINAL_STATUSES.includes(result.status)) {
          setStatus(result);
          onStatusChange && onStatusChange(result);
          return;
        }
      } catch (err) {}

      if (attempts >= MAX_POLL_ATTEMPTS) {
        setTimedOut(true);
        return;
      }

      timeoutHandle = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutHandle);
    };
  }, [uploadId]);

  if (timedOut) {
    return (
      <div className={styles['summary-line']}>
        Still processing — check back in a few minutes.
      </div>
    );
  }

  if (!status) {
    return (
      <div className={styles['summary-line']}>
        <CircularProgress size={16} />
        <span>Validating your file — this usually takes a few seconds.</span>
      </div>
    );
  }

  if (status.status === 'SUCCEEDED') {
    return (
      <div className={`${styles['result-banner']} ${styles.ok}`}>
        <CheckCircleOutlineIcon fontSize="small" />
        <div>
          <strong>{status.successRows} of {status.totalRows} contacts saved</strong>
          <p>Everything in this file was saved successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`${styles['result-banner']} ${styles.warn}`}>
        <WarningAmberOutlinedIcon fontSize="small" />
        <div>
          <strong>{status.successRows} of {status.totalRows} contacts saved</strong>
          <p>
            {status.failedRows} row{status.failedRows === 1 ? '' : 's'} need a fix before they can be added.
            {status.successRows > 0 ? ' Everything else is already saved.' : ''}
          </p>
        </div>
      </div>
      <ContactPreviewTable errors={status.errors} />
    </div>
  );
};
